import type { FC, ReactNode } from 'react'

import { ShoppingBag } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import { useCart, useCartQuote } from '@/lib/cart'
import { Drawer } from '@/components/ui/overlay'
import { ButtonLink } from '@/components/ui/button'
import { CartLineItem } from '@/components/store/cart-line'
import { CartSummary, FreeShippingMeter } from '@/components/store/cart-summary'

export const CartDrawer: FC = (): ReactNode => {
    const { tx } = useI18n()
    const { isOpen, close, setQty, removeItem, lineCount } = useCart()
    const { quote, loading, empty, error } = useCartQuote(null, isOpen)

    const showSkeleton = !empty && !quote && !error

    return (
        <Drawer
            open={isOpen}
            onClose={close}
            title={(
                <span className='flex items-center gap-2'>
                    {tx('سلة التسوق', 'Your cart')}
                    {lineCount > 0 && <span className='rounded-full bg-navy-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-navy-700'>{lineCount}</span>}
                </span>
            )}
            footer={quote && quote.lines.length > 0 ? (
                <div className='flex flex-col gap-4'>
                    <FreeShippingMeter quote={quote} />
                    <CartSummary quote={quote} />

                    <div className='grid grid-cols-2 gap-2'>
                        <ButtonLink href='/cart' variant='outline' onClick={close}>{tx('عرض السلة', 'View cart')}</ButtonLink>
                        <ButtonLink href='/checkout' onClick={close} aria-disabled={!quote.canCheckout} className={quote.canCheckout ? '' : 'pointer-events-none opacity-50'}>
                            {tx('إتمام الطلب', 'Checkout')}
                        </ButtonLink>
                    </div>
                </div>
            ) : undefined}
        >
            <div className='px-5'>
                {empty && (
                    <div className='flex flex-col items-center gap-4 py-16 text-center'>
                        <span className='flex size-16 items-center justify-center rounded-3xl bg-navy-50 text-navy-700'>
                            <ShoppingBag className='size-7' strokeWidth={1.5} aria-hidden='true' />
                        </span>
                        <div className='flex flex-col gap-1'>
                            <p className='font-semibold text-navy-950'>{tx('سلتك فارغة', 'Your cart is empty')}</p>
                            <p className='text-sm text-muted-foreground'>{tx('أضف منتجات وستظهر هنا مع أفضل سعر لكل كمية.', 'Add products and they will appear here with the best price for each quantity.')}</p>
                        </div>
                        <ButtonLink href='/products' onClick={close}>{tx('تسوق المنتجات', 'Shop products')}</ButtonLink>
                    </div>
                )}

                {showSkeleton && (
                    <div className='flex flex-col gap-4 py-5' aria-busy='true'>
                        {[0, 1].map(index => (
                            <div key={index} className='flex gap-4'>
                                <div className='skeleton size-20 rounded-2xl' />
                                <div className='flex flex-1 flex-col gap-2'>
                                    <div className='skeleton h-4 w-3/4 rounded-full' />
                                    <div className='skeleton h-4 w-1/3 rounded-full' />
                                    <div className='skeleton h-9 w-32 rounded-full' />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {error && !quote && (
                    <p className='py-10 text-center text-sm text-red-600' role='alert'>{error}</p>
                )}

                {quote && quote.lines.length > 0 && (
                    <div className={loading ? 'divide-y divide-border opacity-70 transition-opacity' : 'divide-y divide-border transition-opacity'}>
                        {quote.lines.map(line => (
                            <CartLineItem key={line.key} line={line} onQty={setQty} onRemove={removeItem} compact onNavigate={close} />
                        ))}
                    </div>
                )}
            </div>
        </Drawer>
    )
}
